import React, { MouseEventHandler, useCallback, useContext, useRef } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import cn from 'clsx'
import { SearchMenuStates } from '../type'
import DateInputDesktop from './SearchDateInput/SearchDateInputDesktop'
import TextButton from 'LuxKit/Button/TextButton'
import { DATE_SEARCH_OPTION_IDS, LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL, LOCATION_SEARCH_INPUT_TITLE_LABEL } from 'constants/search'
import config from 'constants/config'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import SearchLocationInput from './SearchLocationInput/SearchLocationInput'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import { BusinessTravellerMultiRoomHotelsSelectDesktop, BusinessTravellerMultiRoomHotelsSelectDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import { pushWithRegion } from 'actions/NavigationActions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'
import { connect } from 'react-redux'
import { selectLoggedIn } from 'selectors/accountSelectors'
import NavigationHeader from 'components/App/Header/HeaderMain/NavigationHeader/NavigationHeader'
import Group from 'components/utils/Group'
import MapSearchNav from 'components/NavKit/MapSearchNav'

const InputWrapper = styled.section`
  padding: 0;

  ${mediaQueryUp.tablet} {
    width: 100%;
    max-width: ${rem(1140)};
    position: relative;
    gap: ${rem(12)};
    display: grid;
    grid-template:
      "location-input location-input location-input" auto
      "date-input traveller-input actions" auto;
  }

  ${mediaQueryUp.desktop} {
    grid-template: "location-input date-input actions" auto / 1fr ${rem(320)} auto;
  }
`

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;

  &.location-input {
    grid-area: location-input;
  }

  &.date-input {
    grid-area: date-input;
  }

  &.traveller-input {
    grid-area: traveller-input;
  }

  &.actions {
    grid-area: actions;
  }

  &.arrow {
    grid-area: arrow;
    display: flex;
    align-items: flex-start;
  }

  &.filters {
    grid-area: filters;
    display: flex;
    align-items: flex-end;
  }

  &.login {
    grid-area: login;
  }
`

const SearchButton = styled(TextButton)`
  height: ${rem(50)};
  min-width: ${rem(112)};
`

interface MappedStateProps {
  isLoggedIn: boolean,
}

interface Props {
  rooms: Array<App.Occupants>;
  openBusinessTravellerEmployeeMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openBusinessTravellerRoomMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openDatePickerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openTravellerMenu: (e: React.MouseEvent<HTMLButtonElement>) => void;
  openLocationMenu: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  focusLocationInput: () => void;
  closeMenu: () => void;
  activeMenu: SearchMenuStates;
  onRoomChange: (rooms: Array<App.Occupants>) => void;
  onDatesApply: () => void;
  onDatesDecline: () => void;
  showRoomErrors: boolean;
  anytimeDateSelected?: boolean;
  selectAnytimeDate?: (shouldApply: boolean) => void;
  selectSpecificDates?: () => void;
  selectFlexibleDates?: () => void;
  onFlexibleMonthSelected?: () => void;
  onFlexibleNightsSelected?: () => void;
  dateSearchOptionId?: DATE_SEARCH_OPTION_IDS
  onLocationChange?: (item: App.SearchItem) => void;
  saleUnit?: string;
  typeaheadTypes?: Array<App.SearchPlaceType>;
  recentSearches?: Array<App.FullRecentSearch>;
  switchView?: {
    label: string,
    view: 'map' | 'list'
    URL: string
  }
  locationSearchPlaceholder?: string
  isLocationRequired: boolean
}

const SearchDesktopInputs = React.forwardRef<HTMLInputElement, Props & MappedStateProps>((props, ref) => {
  const {
    activeMenu,
    openBusinessTravellerEmployeeMenu,
    openBusinessTravellerRoomMenu,
    openLocationMenu,
    focusLocationInput,
    closeMenu,
    openDatePickerMenu,
    onDatesApply,
    onDatesDecline,
    anytimeDateSelected,
    selectAnytimeDate,
    selectSpecificDates,
    selectFlexibleDates,
    dateSearchOptionId,
    onLocationChange,
    typeaheadTypes,
    recentSearches,
    switchView,
    locationSearchPlaceholder = LOCATION_SEARCH_INPUT_PLACEHOLDER_LABEL,
    isLoggedIn,
    isLocationRequired,
  } = props
  const dispatch = useAppDispatch()
  const { checkinDate, checkoutDate } = useContext(GlobalSearchStateContext)

  const datesSelected = !!(checkinDate && checkoutDate)

  const isSearchDisabled = !datesSelected

  const toListView = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()

    if (switchView) {
      dispatch(pushWithRegion(switchView.URL))
    }
  }, [switchView, dispatch])

  const isMapPage = switchView?.view === 'list'

  const inputWrapperRef = useRef<HTMLDivElement>(null)

  return (
    <Group direction="vertical" gap={12}>
      <BusinessTravellerAccountGuard
        employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
        loading={<BusinessTravellerSelectLoadingSkeleton />}
        fallback={<BusinessTravellerMultiRoomHotelsSelectDisabled />}
      >
        <BusinessTravellerMultiRoomHotelsSelectDesktop
          activeMenu={activeMenu}
          onMenuDismiss={closeMenu}
          openEmployeeMenu={openBusinessTravellerEmployeeMenu}
          openRoomMenu={openBusinessTravellerRoomMenu}
          focusLocationInput={focusLocationInput}
        />
      </BusinessTravellerAccountGuard>
      <Group direction="horizontal" gap={12} horizontalAlign="center" verticalAlign="center">
        {isMapPage && <CSSBreakpoint min="tablet">
          <TextButton
            kind="tertiary"
            size="large"
            onClick={toListView}
            className="list-view"
            aria-label="list-view"
            data-testid="list-view"
            startIcon={<LineArrowLeftIcon />}
          >
            {switchView.label}
          </TextButton>
        </CSSBreakpoint>}

        <InputWrapper className={cn({ 'map-view': isMapPage })} ref={inputWrapperRef}>
          <InputContainer className="location-input">
            <SearchLocationInput
              label={LOCATION_SEARCH_INPUT_TITLE_LABEL}
              placeholder={locationSearchPlaceholder}
              placeTypes={typeaheadTypes}
              onChange={onLocationChange}
              variant="dropdown"
              inputRef={ref}
              recentSearchItems={recentSearches}
              onLocationToggle={openLocationMenu}
              controlsContainerRef={inputWrapperRef}
              searchVertical="hotel"
              required={isLocationRequired}
            />
          </InputContainer>
          <InputContainer className="date-input">
            <DateInputDesktop
              dropdownAnchorRef={inputWrapperRef}
              onDateToggle={openDatePickerMenu}
              isToggled={activeMenu === SearchMenuStates.Dates}
              onDatesApply={onDatesApply}
              onDatesDecline={onDatesDecline}
              anytimeDateSelected={anytimeDateSelected}
              selectAnytimeDate={selectAnytimeDate}
              selectSpecificDates={selectSpecificDates}
              selectFlexibleDates={selectFlexibleDates}
              dateSearchOptionId={dateSearchOptionId}
              closeMenu={closeMenu}
            />
          </InputContainer>
          <InputContainer className="actions">
            {!isMapPage && <SearchButton disabled={isSearchDisabled} kind="primary" type="submit" size="large">
              Search
            </SearchButton>}
            {isMapPage && <IconButton disabled={isSearchDisabled} kind="primary" type="submit" size="large">
              <LineSearchIcon />
            </IconButton>}
          </InputContainer>
        </InputWrapper>
        {isMapPage && <>
          {config.NAVKIT_NAVBAR_ENABLED && <MapSearchNav />}
          {!config.NAVKIT_NAVBAR_ENABLED && <NavigationHeader isLoggedIn={isLoggedIn} />}
        </>}
      </Group>
    </Group>
  )
})

export default connect<MappedStateProps, undefined, Props, App.State>((state: App.State): MappedStateProps => (
  {
    isLoggedIn: selectLoggedIn(state),
  }
), null, null, { forwardRef: true })(SearchDesktopInputs)
